import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateNominalAPR } from '@prolarus/shared';
import { differenceInDays, parseISO, format } from 'date-fns';

interface Invoice {
  id: string;
  vendor: { name: string } | null;
  invoice_number: string | null;
  total_amount: number | null;
  discount: number | null;
  discount_amt: number | null;
  discount_expiration: string | null;
  due_date: string | null;
}

interface EarlyPayChartProps {
  invoices: Invoice[];
}

interface ChartDataPoint {
  id: string;
  discountAmount: number;
  nominalAPR: number;
  vendorName: string;
  invoiceNumber: string | null;
  daysUntilExpiration: number;
  discountExpiration: string | null;
  totalAmount: number | null;
}

export function EarlyPayChart({ invoices }: EarlyPayChartProps) {
  const navigate = useNavigate();
  const today = new Date();
  const chartTitleEarlyPay = 'Early Pay - Discount Available vs. Nominal APR';

  const chartData: ChartDataPoint[] = invoices
    .map((inv) => {
      const apr = calculateNominalAPR({
        discount: inv.discount,
        dueDate: inv.due_date,
        discountExpiration: inv.discount_expiration,
      });
      const daysUntilExpiration = inv.discount_expiration 
        ? differenceInDays(parseISO(inv.discount_expiration), today)
        : 999;
      return {
        id: inv.id,
        discountAmount: inv.discount_amt ?? 0,
        nominalAPR: apr?.value ? apr.value * 100 : 0,
        vendorName: inv.vendor?.name || 'Unknown',
        invoiceNumber: inv.invoice_number,
        daysUntilExpiration,
        discountExpiration: inv.discount_expiration,
        totalAmount: inv.total_amount,
      };
    })
    .filter((d) => d.nominalAPR > 0 && d.discountAmount > 0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Color scale: Red (urgent) -> Yellow (medium) -> Green (plenty of time)
  // 0-7 days = red, 7-21 days = yellow gradient, 21+ days = green
  const getUrgencyColor = (daysUntilExpiration: number): string => {
    if (daysUntilExpiration <= 0) return 'hsl(0, 80%, 50%)'; // Overdue - bright red
    if (daysUntilExpiration <= 7) {
      // Red zone: 1-7 days
      const intensity = daysUntilExpiration / 7;
      return `hsl(${intensity * 30}, 80%, 50%)`; // Red to orange-red
    }
    if (daysUntilExpiration <= 21) {
      // Yellow zone: 8-21 days
      const intensity = (daysUntilExpiration - 7) / 14;
      return `hsl(${30 + intensity * 30}, 80%, 50%)`; // Orange to yellow
    }
    if (daysUntilExpiration <= 45) {
      // Yellow-green zone: 22-45 days
      const intensity = (daysUntilExpiration - 21) / 24;
      return `hsl(${60 + intensity * 60}, 70%, 45%)`; // Yellow to green
    }
    return 'hsl(120, 60%, 40%)'; // Green - plenty of time
  };

  const handleDotClick = (data: ChartDataPoint) => {
    navigate(`/invoices/${data.id}`);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.vendorName}</p>
          <p className="text-sm text-muted-foreground">
            Invoice: {data.invoiceNumber || 'N/A'}
          </p>
          <p className="text-sm text-muted-foreground">
            Invoice Total: {data.totalAmount != null ? formatCurrency(data.totalAmount) : 'N/A'}
          </p>
          <p className="text-sm text-muted-foreground">
            Discount Amount: {formatCurrency(data.discountAmount)}
          </p>
          <p className="text-sm text-muted-foreground">
            Nominal APR: {formatPercent(data.nominalAPR)}
          </p>
          <p className="text-sm text-muted-foreground">
            Discount Expiration: {data.discountExpiration ? format(parseISO(data.discountExpiration), 'MMM d, yyyy') : 'N/A'}
          </p>
          <p className="text-sm text-muted-foreground">
            Expires in: {data.daysUntilExpiration <= 0 ? 'Overdue!' : `${data.daysUntilExpiration} days`}
          </p>
          <p className="text-xs text-primary mt-2">Click to open invoice</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
        <CardTitle>{chartTitleEarlyPay}</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No data available for chart</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{chartTitleEarlyPay}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                type="number"
                dataKey="discountAmount"
                name="Discount Amount"
                tickFormatter={formatCurrency}
                label={{ value: 'Discount Amount', position: 'bottom', offset: 0 }}
                className="text-muted-foreground"
              />
              <YAxis
                type="number"
                dataKey="nominalAPR"
                name="Nominal APR"
                tickFormatter={formatPercent}
                label={{ value: 'Nominal APR', angle: -90, position: 'insideLeft' }}
                className="text-muted-foreground"
              />
              <Tooltip content={<CustomTooltip />} />
              <Scatter
                name="Invoices"
                data={chartData}
                cursor="pointer"
                onClick={(data) => handleDotClick(data as unknown as ChartDataPoint)}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getUrgencyColor(entry.daysUntilExpiration)}
                    fillOpacity={0.85}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
